package com.bulut.mecitogullari.ui.screens.newproject

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.Check
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.compose.material.icons.filled.Close
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.TextButton
import com.bulut.mecitogullari.ui.components.SectionTitle
import com.bulut.mecitogullari.core.i18n.LocalStrings
import com.bulut.mecitogullari.core.i18n.fillTemplate
import com.bulut.mecitogullari.ui.components.DateField
import com.bulut.mecitogullari.ui.theme.AppColors
import com.bulut.mecitogullari.ui.theme.parseHexColor
import org.koin.compose.viewmodel.koinViewModel

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun NewProjectScreen(
    onClose: () -> Unit,
    viewModel: NewProjectViewModel = koinViewModel(),
) {
    val uiState by viewModel.uiState.collectAsStateWithLifecycle()
    val strings = LocalStrings.current

    LaunchedEffect(uiState.saved) {
        if (uiState.saved) {
            viewModel.onSavedHandled()
            onClose()
        }
    }

    Scaffold(
        containerColor = AppColors.Background,
        topBar = {
            TopAppBar(
                title = { Text(strings.newProject) },
                navigationIcon = {
                    IconButton(onClick = onClose) {
                        Icon(Icons.Default.ArrowBack, contentDescription = strings.back)
                    }
                },
            )
        },
    ) { padding ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding)
                .verticalScroll(rememberScrollState())
                .padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(14.dp),
        ) {
            OutlinedTextField(
                value = uiState.name,
                onValueChange = viewModel::onNameChange,
                label = { Text(strings.projectNameLabel) },
                singleLine = true,
                shape = RoundedCornerShape(12.dp),
                modifier = Modifier.fillMaxWidth(),
            )

            OutlinedTextField(
                value = uiState.location,
                onValueChange = viewModel::onLocationChange,
                label = { Text(strings.locationLabel) },
                singleLine = true,
                shape = RoundedCornerShape(12.dp),
                modifier = Modifier.fillMaxWidth(),
            )

            Row(horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                DateField(
                    label = strings.startLabel,
                    dateEpochMillis = uiState.startDateEpochMillis,
                    onDateSelected = viewModel::onStartDateChange,
                    modifier = Modifier.weight(1f),
                )
                DateField(
                    label = strings.estimatedEndLabel,
                    dateEpochMillis = uiState.expectedEndEpochMillis,
                    onDateSelected = viewModel::onEndDateChange,
                    modifier = Modifier.weight(1f),
                )
            }

            SectionTitle(strings.blocksSection)
            Text(
                text = strings.blocksHint,
                style = MaterialTheme.typography.bodySmall,
                color = AppColors.TextSecondary,
            )

            uiState.blocks.forEachIndexed { index, block ->
                BlockInputRow(
                    index = index,
                    block = block,
                    showName = uiState.blocks.size > 1,
                    showRemove = uiState.blocks.size > 1,
                    onNameChange = { viewModel.onBlockNameChange(index, it) },
                    onFloorsChange = { viewModel.onFloorsChange(index, it) },
                    onPerFloorChange = { viewModel.onApartmentsPerFloorChange(index, it) },
                    onRemove = { viewModel.onRemoveBlock(index) },
                )
            }

            TextButton(onClick = viewModel::onAddBlock) {
                Text(strings.addBlock, color = AppColors.Navy)
            }

            OutlinedTextField(
                value = uiState.notes,
                onValueChange = viewModel::onNotesChange,
                label = { Text(strings.noteOptional) },
                minLines = 3,
                shape = RoundedCornerShape(12.dp),
                modifier = Modifier.fillMaxWidth(),
            )

            Column {
                Text(
                    text = strings.coverColorLabel,
                    style = MaterialTheme.typography.labelLarge,
                    color = AppColors.TextSecondary,
                )
                Row(
                    horizontalArrangement = Arrangement.spacedBy(10.dp),
                    modifier = Modifier.padding(top = 8.dp),
                ) {
                    AppColors.CoverOptions.forEach { hex ->
                        val selected = uiState.coverColorHex == hex
                        Box(
                            modifier = Modifier
                                .size(38.dp)
                                .background(parseHexColor(hex), CircleShape)
                                .border(
                                    width = if (selected) 3.dp else 0.dp,
                                    color = if (selected) AppColors.NavyDeep else Color.Transparent,
                                    shape = CircleShape,
                                )
                                .clickable { viewModel.onCoverColorChange(hex) },
                            contentAlignment = Alignment.Center,
                        ) {
                            if (selected) {
                                Icon(
                                    Icons.Default.Check,
                                    contentDescription = null,
                                    tint = Color.White,
                                    modifier = Modifier.size(18.dp),
                                )
                            }
                        }
                    }
                }
            }

            uiState.error?.let { error ->
                Text(
                    text = error,
                    color = AppColors.Expense,
                    style = MaterialTheme.typography.bodySmall,
                )
            }

            Button(
                onClick = viewModel::save,
                enabled = !uiState.isSaving,
                shape = RoundedCornerShape(12.dp),
                colors = ButtonDefaults.buttonColors(containerColor = AppColors.Navy),
                modifier = Modifier.fillMaxWidth().height(52.dp),
            ) {
                Text(strings.createProject, style = MaterialTheme.typography.titleMedium, color = Color.White)
            }
        }
    }
}

/** One block of the new project: optional name + its own floors/units. */
@Composable
private fun BlockInputRow(
    index: Int,
    block: BlockInput,
    showName: Boolean,
    showRemove: Boolean,
    onNameChange: (String) -> Unit,
    onFloorsChange: (String) -> Unit,
    onPerFloorChange: (String) -> Unit,
    onRemove: () -> Unit,
) {
    val strings = LocalStrings.current
    Card(
        shape = RoundedCornerShape(14.dp),
        colors = CardDefaults.cardColors(containerColor = Color.White),
        elevation = CardDefaults.cardElevation(defaultElevation = 1.dp),
        modifier = Modifier.fillMaxWidth(),
    ) {
        Column(
            modifier = Modifier.padding(12.dp),
            verticalArrangement = Arrangement.spacedBy(10.dp),
        ) {
            if (showName) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    OutlinedTextField(
                        value = block.name,
                        onValueChange = onNameChange,
                        label = { Text(strings.blockNameLabel) },
                        singleLine = true,
                        shape = RoundedCornerShape(12.dp),
                        modifier = Modifier.weight(1f),
                    )
                    if (showRemove) {
                        IconButton(onClick = onRemove) {
                            Icon(
                                Icons.Default.Close,
                                contentDescription = strings.removeBlock,
                                tint = AppColors.Expense,
                            )
                        }
                    }
                }
            }
            Row(horizontalArrangement = Arrangement.spacedBy(10.dp)) {
                OutlinedTextField(
                    value = block.floorsText,
                    onValueChange = onFloorsChange,
                    label = { Text(strings.floorCountLabel) },
                    singleLine = true,
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                    shape = RoundedCornerShape(12.dp),
                    modifier = Modifier.weight(1f),
                )
                OutlinedTextField(
                    value = block.apartmentsPerFloorText,
                    onValueChange = onPerFloorChange,
                    label = { Text(strings.unitsPerFloorLabel) },
                    singleLine = true,
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                    shape = RoundedCornerShape(12.dp),
                    modifier = Modifier.weight(1f),
                )
            }
            val floors = block.floorsText.toIntOrNull() ?: 0
            val perFloor = block.apartmentsPerFloorText.toIntOrNull() ?: 0
            if (floors > 0 && perFloor > 0) {
                Text(
                    text = strings.blockLayoutSummary.fillTemplate(
                        "floors" to floors.toString(),
                        "perFloor" to perFloor.toString(),
                    ) + " = ${floors * perFloor} ${strings.units}",
                    style = MaterialTheme.typography.labelMedium,
                    color = AppColors.TextSecondary,
                )
            }
        }
    }
}
